import { useEffect, useRef } from "react";
import { useEventCallback } from "usehooks-ts";

import { useWindowVisibility } from "./use-window-visibility";

/**
 * Invoke `onRise` once on each hidden→visible transition (a rising visibility edge) — the shared
 * "recover on tab-return" primitive. `onRise` is stabilized via `useEventCallback` so it always
 * sees the latest closure without re-running the edge effect, and the caller does any gating inside
 * the callback (it fires on every rise). No-op on the initial mount and on visible→hidden.
 */
export function useVisibilityRisingEdge(onRise: () => void): void {
  const stableOnRise = useEventCallback(onRise);
  const isVisible = useWindowVisibility();
  const wasVisibleRef = useRef(isVisible);
  useEffect(() => {
    const roseToVisible = !wasVisibleRef.current && isVisible;
    wasVisibleRef.current = isVisible;
    if (roseToVisible) {
      stableOnRise();
    }
  }, [isVisible, stableOnRise]);
}
